import {
  CreditCardOutlined,
  CrownOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { useLandmineOpensCRUDActions } from '@mm/state/actions/LandmineOpensCRUDActions';
import subscriptionCRUDActions from '@mm/state/actions/SubscriptionCRUDActions';
import viewActions from '@mm/state/actions/viewActions';
import { isTeamTrial } from '@mm/state/helpers/billing-subscription-tab-helpers';
import { EnhancedBillingPage } from '@mm/state/interfaces/connected-page';
import modalActions, {
  useModalActions,
} from '@mm/state/modules/modalsState/actions';
import {
  activeFacebookPageSelector,
  enhancedBillingPagesSelector,
  selectActiveFacebookPageFromSession,
  selectBasicPages,
} from '@mm/state/selectors/facebookPageSelector';
import {
  defaultProUnicornPlanSelector,
  defaultTeamPlanSelector,
  formattedInvoicesSelector,
  hasTeamSubscriptionSelector,
  selectAccountDetails,
} from '@mm/state/selectors/subscriptionSelectors';
import { companyAdminSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import queryString from 'query-string';
import React, { useEffect, useMemo, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { compose } from 'recompose';
import { Dispatch } from 'redux';
import { OVERAGES_ENABLED } from '../../../../common/constants/overagesText';
import { RouterComponentProps } from '../../../../common/helpers/routes/interfaces';
import { useActivateSmsRedirectCallback } from '../../../../common/hooks/use-activate-sms-redirect-callback';
import './BillingPanel.pcss';
import SubscriptionTabsArea from './SubscriptionTabsArea/SubscriptionTabsArea';
import { SettingsSectionHeader } from '../common/SettingsSectionHeader/SettingsSectionHeader';
import { isBilling2023Subscription } from '@mm/state/model/SubscriptionModel';
import { BookDemoButton } from '../../../../common/buttons/BookDemoButton/BookDemoButton';
import { useBookDemo } from '../../../../common/buttons/BookDemoButton/use-book-demo';
import { FreeTrialCountdown } from '../../../../layout/BotBuilderSidebar/FreeTrialCountdown/FreeTrialCountdown';
import { BILLING_PAGE_UTM } from '../../XRayOnboarding/WebsiteReportSummary/constants';

export type BillingPannelContextType = {
  landmineOpenId: number | undefined;
};
export const BillingPannelContext = React.createContext(
  {} as BillingPannelContextType,
);

type OuterProps = {};

type InnerProps = ReturnType<typeof mapDispatchToProps> &
  ReturnType<typeof mapStateToProps> &
  RouterComponentProps;

export type BillingPanelProps = OuterProps & InnerProps;

const BillingPanel = (props: BillingPanelProps) => {
  const {
    facebookPages,
    defaultUnicornPlan,
    defaultTeamPlan,
    hasTeamSubscription,
    accountDetails,
    invoices,
    isCompanyAdmin,
  } = props;
  const dispatch = useDispatch();
  const [searchInput, setSearchInput] = useState('');
  const teamTrial = isTeamTrial(facebookPages);
  const editBillingLink =
    accountDetails && accountDetails.account
      ? accountDetails.account.editBillingLink
      : '';
  const [overagesEnabled, setOveragesEnabled] = useState(false);
  const [landmineOpenId, setLandmineOpenId] = useState<number | undefined>(
    undefined,
  );
  const [landmineStatusUpdated, setLandmineStatusUpdated] = useState(false);
  const reduxViewLandmineOpenId = useSelector(
    (state: StoreState) => state.view.landmineOpenId,
  );
  const landmineOpensCRUDActions = useLandmineOpensCRUDActions();
  const { isEligibleForDemo } = useBookDemo();

  const facebookPageFromDetails = useSelector(activeFacebookPageSelector);
  const facebookPageFromSession = useSelector(
    selectActiveFacebookPageFromSession,
  );
  const enhancedPages = useSelector(enhancedBillingPagesSelector);
  const facebookPage =
    facebookPageFromDetails ||
    enhancedPages.find(p => p.id === facebookPageFromSession?.id);

  const isNewBillingSubscription =
    facebookPage?.subscription &&
    isBilling2023Subscription(facebookPage.subscription);

  // Note: we need to pass ladmineOpenId from view state to the Billing Modal
  // once for the first open after setting it only. That's why we read and reset it.
  useEffect(() => {
    if (reduxViewLandmineOpenId) {
      setLandmineOpenId(reduxViewLandmineOpenId);
      dispatch(viewActions.setLandmineOpenId(undefined));
    }
  }, []);

  useEffect(() => {
    const enableOverages = async () => {
      const {
        location: { search },
      } = props;
      const parsed = queryString.parse(search);
      const facebookPageId =
        typeof parsed.fbPageId === 'string' && parsed.fbPageId;
      const subscriptionId = typeof parsed.subId === 'string' && parsed.subId;
      const { src, landmineOpenId: urlLandmineOpenId } = parsed;
      if (
        urlLandmineOpenId &&
        typeof urlLandmineOpenId === 'string' &&
        urlLandmineOpenId !== 'nil'
      ) {
        setLandmineOpenId(Number(urlLandmineOpenId));
        if (!landmineStatusUpdated) {
          landmineOpensCRUDActions.update(Number(urlLandmineOpenId), true);
          setLandmineStatusUpdated(true);
        }
      }

      if (facebookPageId && subscriptionId && src === 'productUpdates') {
        const { sendOveragesEnabled, openAllSetModal } = props;
        await sendOveragesEnabled(
          parseInt(subscriptionId, 10),
          true,
          parseInt(facebookPageId, 10),
          isNewBillingSubscription,
        );

        setOveragesEnabled(true);
        openAllSetModal(OVERAGES_ENABLED);
      }
    };

    if (!overagesEnabled) enableOverages();
  });

  const onSuccessBilling = useActivateSmsRedirectCallback();
  const modalActionsMap = useModalActions();

  useEffect(() => {
    const sp = queryString.parse(props.location.search) || {};
    const { fbPageId, src, landmineId } = sp;

    const landmineIdInt = parseInt(landmineId as string, 10);
    const pageId = parseInt(fbPageId as string, 10);

    const page = facebookPages.find(({ id }) => id === pageId);

    if (fbPageId && src === 'trial' && defaultUnicornPlan && page) {
      modalActionsMap.openBillingModal({
        plan: defaultUnicornPlan,
        landmineOpenId,
        stayOnPage: false,
        onSuccessBilling,
        facebookPage: page as EnhancedBillingPage,
      });

      if (landmineId) {
        setLandmineOpenId(landmineIdInt);
        if (!landmineStatusUpdated) {
          landmineOpensCRUDActions.update(landmineIdInt, true);
          setLandmineStatusUpdated(true);
        }
      }
    }
  }, [props.location.search]);

  const contextValue = useMemo(() => ({ landmineOpenId }), [landmineOpenId]);

  return (
    <BillingPannelContext.Provider value={contextValue}>
      <div className="billing-panel">
        {accountDetails && (
          <>
            <SettingsSectionHeader
              title={
                <div className="billing-panel__header-left">
                  <span>Your Subscriptions</span>

                  <div className="billing-panel__header-links">
                    {isCompanyAdmin && editBillingLink && (
                      <div
                        onClick={() => window.open(editBillingLink, '_blank')}
                        className="billing-panel__header-link"
                      >
                        <CreditCardOutlined />
                        Edit Billing Info
                      </div>
                    )}

                    {isCompanyAdmin && invoices && !!invoices.length && (
                      <div
                        className="billing-panel__header-link"
                        onClick={() => modalActionsMap.openInvoiceModal()}
                      >
                        <UnorderedListOutlined />
                        View Billing History
                      </div>
                    )}
                  </div>
                </div>
              }
              color="#EBA621"
              icon={<CrownOutlined />}
              headerComponent={
                <div className="billing-panel__section-header">
                  {isEligibleForDemo && (
                    <div className="billing-panel__upgrade-team-section">
                      <div className="billing-panel__upgrade-team-section-text">
                        Run an agency? Need to add more accounts? Let's chat
                      </div>
                      <BookDemoButton
                        button="Book a Demo now"
                        className="billing-panel__secondary-button"
                        utm={BILLING_PAGE_UTM('scheduleademo')}
                      />
                    </div>
                  )}
                </div>
              }
            />
            <FreeTrialCountdown />
            <SubscriptionTabsArea
              searchInput={searchInput}
              setSearchInput={setSearchInput}
            />
          </>
        )}
      </div>
    </BillingPannelContext.Provider>
  );
};

const mapStateToProps = (state: StoreState) => ({
  invoices: formattedInvoicesSelector(state),
  facebookPages: selectBasicPages(state),
  defaultTeamPlan: defaultTeamPlanSelector(state),
  defaultUnicornPlan: defaultProUnicornPlanSelector(state),
  accountDetails: selectAccountDetails(state),
  hasTeamSubscription: hasTeamSubscriptionSelector(state),
  isCompanyAdmin: companyAdminSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  openAllSetModal: (message: string) =>
    dispatch(modalActions.openAllSetModal({ message })),
  sendOveragesEnabled: (
    subscriptionId: number,
    overagesEnabled: boolean,
    facebookPageId: number,
    useV2Route?: boolean,
  ) =>
    dispatch(
      subscriptionCRUDActions.sendOveragesEnabled(
        subscriptionId,
        overagesEnabled,
        facebookPageId,
        false,
        undefined,
        false,
        useV2Route,
      ),
    ),
});

const enhance = compose<InnerProps, OuterProps>(
  connect(mapStateToProps, mapDispatchToProps),
);

export default enhance(BillingPanel);
